import { useEditor, EditorContent, EditorContext } from '@tiptap/react';
import { Placeholder } from '@tiptap/extension-placeholder';
import TextAlign from '@tiptap/extension-text-align';
import Underline from '@tiptap/extension-underline';
import Document from '@tiptap/extension-document';
import { BubbleMenu } from '@shared/ui/tiptap';
import Italic from '@tiptap/extension-italic';
import StarterKit from '@tiptap/starter-kit';
import { Toolbar } from '@shared/ui/tiptap';
import * as stylex from '@stylexjs/stylex';
import { useMemo, useState, useEffect } from 'react';
import Link from '@tiptap/extension-link';
import Text from '@tiptap/extension-text';
import Bold from '@tiptap/extension-bold';
import { styles } from './styles';

export interface TiptapProps {
  onChange: (html: string) => void;
  className?: string;
  placeholder?: string;
  defaultAlignment?: string;
  properties?: unknown;
  isInline?: boolean;
  hiddenStyles?: string[];
  content?: string;
  editable?: boolean;
  withToolbar?: boolean;
}

export const Tiptap = ({
  className,
  properties,
  placeholder,
  defaultAlignment,
  onChange,
  isInline,
  hiddenStyles,
  content,
  editable = true,
  withToolbar,
}: TiptapProps) => {
  const [isEmpty, setIsEmpty] = useState(!content);

  const editor = useEditor({
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
      setIsEmpty(!editor.getJSON().content);
    },
    extensions: [
      ...(isInline
        ? [
            Bold,
            Italic,
            Underline,
            Link,
            Text,
            Document.extend({
              content: 'inline*',
            }),
          ]
        : [
            StarterKit,
            TextAlign.configure({
              defaultAlignment: defaultAlignment || 'left',
              types: ['heading', 'paragraph'],
            }),
          ]),
      Placeholder.configure({
        placeholder: placeholder || 'Введите текст',
      }),
    ],
    content: content || '',
    editorProps: {
      attributes: {
        class: isInline && isEmpty ? 'is-empty is-editor-empty' : '',
        'data-placeholder': isEmpty ? placeholder || 'Введите текст' : '',
      },
    },
    editable,
  });

  const providerValue = useMemo(() => ({ editor }), [editor]);

  useEffect(() => {
    if (!editor) return;
    editor.setEditable(editable);
  }, [editor, editable]);

  const handleDragStart = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const getMenu = () =>
    withToolbar ? (
      <Toolbar editor={editor} properties={properties} hiddenStyles={hiddenStyles} />
    ) : (
      <BubbleMenu editor={editor} properties={properties} hiddenStyles={hiddenStyles} />
    );

  return (
    <div {...stylex.props(styles.tiptap)} draggable={editable} onDragStart={handleDragStart}>
      <EditorContext.Provider value={providerValue}>
        {editable && editor && getMenu()}
        <EditorContent editor={editor} className={className} />
      </EditorContext.Provider>
    </div>
  );
};
